<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import type { ICPFileUploadPropTypes } from '~/portal/components/base'
import { CPButton, CPFileUploadItem } from '~/portal/components/base'

import { formatFileSize } from './index.ts'

const props = withDefaults(defineProps<ICPFileUploadPropTypes>(), {
	accept: 'application/pdf',
	maxSize: 512,
	isEditable: true,
	multiple: false,
	allowDownload: true
})

const emit = defineEmits<{
	(e: 'click'): void
	(e: 'delete'): void
	(e: 'update:modelValue', payload: any): void
	(e: 'error', size: string): void
	(e: 'manual-change'): void
}>()

const inputRef = ref()
const isDelete = ref(false)
const fileUrls = ref<string[]>([])

const model = computed({
	set: value => {
		emit('update:modelValue', value)
	},
	get: () => props.modelValue
})

const onClick = (): void => {
	emit('click')
	inputRef.value.click()
}

const onDeleteFile = (value?: number): void => {
	isDelete.value = true
	if (model.value?.length > 1 && (value === 0 || value)) {
		model.value = [
			...model.value.slice(0, value),
			...model.value.slice(value + 1)
		]
	} else {
		model.value = undefined
	}
	emit('delete')
	emit('manual-change')
}

const onFilesAdded = (value: any) => {
	emit('manual-change')
	model.value = value
}

const fileName = computed(() => {
	return Array.isArray(model.value) ? model.value[0]?.name : model.value.name
})

const fileSize = computed(() => {
	return Array.isArray(model.value) ? model.value[0]?.size : model.value?.size
})

watch(model, (value, oldValue) => {
	if (props.multiple) {
		if (!checkRAWFile(value) && !!checkRAWFile(oldValue)) {
			return
		}
		if (oldValue?.length && !isDelete.value) {
			model.value?.push(...oldValue)
		}
	}
	isDelete.value = false
	if (model.value && model.value[0]) {
		if (value[0]?.size > props.maxSize * 1024 * 1024) {
			emit('error', formatFileSize(value[0]?.size))
			model.value = undefined
		}
	}
})

const checkRAWFile = (value: any) => {
	return value?.every((item: any) => {
		return !(item instanceof File)
	})
}

const blobUrlToArrayBuffer = async (blobUrl: string) => {
	const response = await fetch(blobUrl)
	return await response.blob()
}

const setFileList = async () => {
	if (!checkRAWFile(model.value) || !model.value?.length) return model.value
	const list: Promise<Blob>[] = model.value?.map(async (item: any) => {
		return await blobUrlToArrayBuffer(item.file_key as string)
	})
	const values = await Promise.all(list)
	return values.map((value, index) => {
		const src = model.value[index]
		const f = new File([value], src?.name || `file_${index + 1}`, {
			type: value.type
		}) as File & {
			__serverMeta__?: { id?: number | string; file_key?: string }
		}
		Object.defineProperty(f, '__serverMeta__', {
			value: { id: src?.id, file_key: src?.file_key },
			enumerable: false,
			configurable: false,
			writable: false
		})
		return f
	})
}

const getFileUrl = (file: File) => {
	const meta = (file as any).__serverMeta__
	return props.allowDownload || props.allowPreview
		? (meta?.file_key ??
				fileUrls.value?.find((item: string) => item?.includes(file.name)))
		: undefined
}

onMounted(async () => {
	fileUrls.value = model.value?.map((item: any) => item.file_key)
	model.value = await setFileList()
})
</script>

<script lang="ts">
export default { inheritAttrs: false }
</script>

<template>
	<div
		:class="[
			'cp-file-upload',
			{
				'cp-file-upload--multiple': multiple
			},
			$attrs.class
		]"
	>
		<CPButton
			v-if="!model || multiple"
			class="cp-file-upload__button"
			:disabled="disabled"
			color="accent"
			icon-prepend="upload"
			@click="onClick"
		>
			{{ label || 'Upload von Dokumenten' }}
			<VFileInput
				ref="inputRef"
				class="cp-file-upload__input"
				:accept="accept"
				:multiple="multiple"
				:model-value="model"
				@update:model-value="onFilesAdded"
			/>
		</CPButton>

		<div class="cp-file-upload__list" v-if="Array.isArray(model)">
			<CPFileUploadItem
				v-for="(file, index) in model"
				:key="file.name"
				:name="file.name"
				:size="file.size"
				:is-editable="isEditable"
				:allow-download="allowDownload"
				:allow-preview="allowPreview"
				:download-link="getFileUrl(file)"
				@delete="onDeleteFile(index)"
				@manual-change="$emit('manual-change')"
			/>
		</div>

		<CPFileUploadItem
			v-else-if="model"
			:name="fileName"
			:size="fileSize"
			:is-editable="isEditable"
			:allow-download="allowDownload"
			:allow-preview="allowPreview"
			:download-link="getFileUrl(model)"
			@delete="onDeleteFile"
			@manual-change="$emit('manual-change')"
		/>
	</div>
</template>

<style lang="scss">
.cp-file-upload {
	@apply inline-flex;

	&--multiple {
		@apply flex-wrap gap-6;

		.cp-file-upload__button {
			@apply order-last;
		}

		.cp-file-upload__list {
			@apply w-full;
		}
	}

	&__button {
		@apply relative cursor-pointer overflow-hidden;
	}

	&__input {
		&,
		&.v-input {
			@apply hidden invisible;
		}
	}

	&__list {
		@apply grid gap-6;
	}
}
</style>
